import { usePostPartnerMutation } from '@/api/api.slice.tsx';
import { PartnerType } from '@/api/types.ts';
import { useCreateSetupIntentMutation } from '@/features/means-of-payments/api/useCreateSetupIntentMutation.ts';
import { useCreateWireTransferSetupMutation } from '@/features/means-of-payments/api/useCreateWireTransferSetupMutation.ts';
import { useFinalizePaymentMutation } from '@/features/means-of-payments/api/useFinalizePaymentMutation.ts';
import { useGetAvailablePaymentListQuery } from '@/features/means-of-payments/api/useGetAvailablePaymentListQuery.ts';
import { WireTransferCallout } from '@/features/means-of-payments/WireTransferCallout.tsx';
import { WireTransferElement } from '@/features/means-of-payments/WireTransferElement.tsx';
import { PaymentStepFormType } from '@/features/OnBoarding/StepPayment/types.ts';
import { useGetPaymentMethod } from '@/features/OnBoarding/StepPayment/useGetPaymentMethod.tsx';
import {
  Alert,
  CollectMeanOfPayment,
  getMessageFromError,
  Loader,
  PaymentContextProvider,
  PossibleLocalesType,
  toastError,
  Typography,
} from '@arsnovasystems/ars-front-lib';
import { Box, Stack } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

/**
 * StripeSection part of the stepPayment
 *
 * @param props    props of the component
 * @param props.me the partner currently filling form
 *
 * @returns        JSX of the component
 */
export const StripeSection = ({ me }: { me: PartnerType }) => {
  const { t, i18n } = useTranslation();
  const { trigger, getValues } = useFormContext<PaymentStepFormType>(); // retrieve all hook methods
  const { meanOfPayment, error, isLoading, refetch } = useGetPaymentMethod();

  const stripePublicKey = import.meta.env.ARS_STRIPE_PUBLIC_KEY;
  const [createIntentTrigger] = useCreateSetupIntentMutation();
  const [finalizeIntentTrigger] = useFinalizePaymentMutation();
  const [createWireTransferSetupTrigger] = useCreateWireTransferSetupMutation();
  const [savePartnerTrigger, savePartnerResult] = usePostPartnerMutation();

  const { data: availablePaymentList } = useGetAvailablePaymentListQuery();

  const isPaymentMethodSetup = meanOfPayment !== undefined;

  const billingDetails = {
    name: me.lastName,
    phone: me.phoneNumber,
    email: me.email,
    address: {
      country: me.country?.countryCode ?? undefined,
      line1: me.address,
      city: me.town,
      postal_code: me.zipCode,
      state: me.stateRegion,
    },
  };

  return (
    <>
      {isLoading ?
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 100,
          }}
        >
          <Loader />
        </Stack>
      : null}

      {!isLoading && !isPaymentMethodSetup && availablePaymentList ?
        <Box
          sx={{
            marginTop: 2,
            marginBottom: 2,
          }}
        >
          <PaymentContextProvider
            createIntentMutationTrigger={createIntentTrigger}
            finalizeIntentMutationTrigger={finalizeIntentTrigger}
            WireTransferElement={WireTransferElement}
            locale={i18n.language as PossibleLocalesType}
            currency={me.currency}
            stripePublicKey={stripePublicKey}
            billingDetails={billingDetails}
            possiblePaymentList={availablePaymentList.availablePayments}
            beforeSubmit={async () => {
              return await trigger();
            }}
            onSuccess={async (paymentType) => {
              // Wire transfer is no longer handled inside the library: provision
              // its funding setup here before persisting the partner.
              if (paymentType === 'wire_transfer') {
                const result = await createWireTransferSetupTrigger();
                if (result.error) {
                  toastError({
                    description: getMessageFromError(result.error),
                  });
                  return;
                }
              }
              void savePartnerTrigger({
                ...me,
                accountingEmail: getValues().accountingEmail,
                companyVatNumber: getValues().companyVatNumber,
              });
              refetch();
            }}
          >
            <CollectMeanOfPayment />
          </PaymentContextProvider>
        </Box>
      : null}

      {!isLoading && isPaymentMethodSetup ?
        <Box>
          <Alert
            sx={{
              alignItems: 'center',
            }}
            severity={'info'}
            compact={true}
          >
            <Typography variant={'body2'}>
              {t('ik.your.payment.method.has.been.saved ({{ paymentMode }})', {
                paymentMode: t('payment.' + meanOfPayment.type),
              })}
            </Typography>
          </Alert>
          {meanOfPayment.type === 'customer_balance' && (
            <>
              <Typography sx={{ mt: 2 }}>
                {t(
                  'ik.you.will.be.able.to.review.this.information.from.your.partner.console',
                )}
              </Typography>
              <WireTransferCallout displayOnly={true} />
            </>
          )}
        </Box>
      : null}

      {error ?
        <Alert>{getMessageFromError(error)}</Alert>
      : null}
      {savePartnerResult.error ?
        <Alert>{getMessageFromError(savePartnerResult.error)}</Alert>
      : null}
    </>
  );
};
